import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, ScrollText, KeyRound, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { buildWillClause, printCertificate } from "@/lib/certificate";

export const CodeReveal = ({ result }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const willClause = buildWillClause(result.title, result.code);

  const copyCode = async () => {
    await navigator.clipboard.writeText(result.code);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyClause = async () => {
    await navigator.clipboard.writeText(willClause);
    toast.success("Will clause copied");
  };

  return (
    <div className="min-h-screen bg-obsidian bg-grain">
      <Navbar />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-32 pb-20 text-center">
        <div className="animate-fade-up" style={{ opacity: 0 }}>
          <span className="inline-grid place-items-center w-16 h-16 rounded-full border border-gold/40 text-gold mb-6 animate-flicker">
            <KeyRound className="w-7 h-7" />
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-gold-light text-glow-gold">Your message is sealed</h1>
          <p className="text-slate-400 mt-4 max-w-md mx-auto">
            This is your unlock code. Write it down and keep it safe — it's the only way to open your message.
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8 mt-10 animate-fade-up" style={{ animationDelay: "0.15s", opacity: 0 }}>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-4">Unlock Code</p>
          <div data-testid="generated-code" className="font-mono text-3xl sm:text-4xl tracking-[0.2em] font-bold text-gold break-all">
            {result.code}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button data-testid="copy-code-button" onClick={copyCode} className="flex-1 bg-gold hover:bg-gold-hover text-obsidian font-semibold rounded-full h-11">
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy code</>}
            </Button>
            <Button data-testid="copy-clause-button" onClick={copyClause} variant="outline" className="flex-1 rounded-full h-11 border-gold/40 text-gold-light bg-transparent hover:bg-gold/10">
              <ScrollText className="w-4 h-4 mr-1" /> Copy will clause
            </Button>
          </div>
          <Button data-testid="print-certificate-button" onClick={() => printCertificate(result.title, result.code)} variant="ghost" className="mt-3 text-slate-400 hover:text-gold hover:bg-transparent">
            <FileText className="w-4 h-4 mr-1" /> Print certificate
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 animate-fade-up" style={{ animationDelay: "0.3s", opacity: 0 }}>
          <Button data-testid="goto-vault-button" onClick={() => navigate("/vault")} variant="outline" className="rounded-full border-gold/30 text-slate-200 bg-transparent hover:bg-gold/10 hover:text-gold">
            View my vault
          </Button>
          <Button data-testid="record-another-button" onClick={() => navigate("/record")} variant="ghost" className="rounded-full text-slate-400 hover:text-gold hover:bg-transparent">
            Record another
          </Button>
        </div>
      </div>
    </div>
  );
};
